import React, { ReactNode } from "react";
import { Alert, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";

import { getAnalyticsWidget, getGenres } from "../../../utils/Requests";
import { getDefaultDateRange } from "../../../utils/analytics";
import { AnalyticsFilters, AnalyticsFilterOptions } from "../../../types/analytics";
import AnalyticsFiltersComponent from "../../../components/analytics/AnalyticsFilters";
import MetricCards, { MetricCardItem } from "../../../components/analytics/MetricCards";
import Logger from "../../../utils/Logger";

type ContentGenreExplorerProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type ContentGenreExplorerState = {
    isGenresLoading: boolean;
    isLoading: boolean;
    error: string | null;
    filters: AnalyticsFilters;
    appliedFilters: AnalyticsFilters;
    filterOptions: AnalyticsFilterOptions;
    genres: string[];
    selectedGenre: string;
    metricsData: Record<string, any>[];
    topBrandsData: TopBrandPoint[];
};

type TopBrandPoint = {
    thingBrandName: string;
    plays: number;
};

type GenreMetricCard = {
    key: string;
    label: string;
    value: number | null;
    changePercent: number | null;
    numericChange: number | null;
    valueFormat: "number" | "percent" | "decimal";
};

class ContentGenreExplorer extends React.Component<ContentGenreExplorerProps, ContentGenreExplorerState> {
    constructor(props: ContentGenreExplorerProps) {
        super(props);

        const { startDate, endDate } = getDefaultDateRange();
        const initialFilters: AnalyticsFilters = {
            startDate,
            endDate,
            attributionId: [],
            brandId: [],
            deviceType: []
        };

        this.state = {
            isGenresLoading: true,
            isLoading: false,
            error: null,
            filters: initialFilters,
            appliedFilters: initialFilters,
            filterOptions: {
                attributionIds: [],
                brandIds: [],
                deviceTypes: []
            },
            genres: [],
            selectedGenre: "",
            metricsData: [],
            topBrandsData: []
        };
    }

    componentDidMount(): void {
        this.initializePage();
    }

    private initializePage = async (): Promise<void> => {
        try {
            this.setState({ isGenresLoading: true, error: null });

            const genreResponse = await getGenres(this.props.customerConfig);
            const genreBody = await genreResponse.json();
            const rawGenres: unknown = genreBody?.items?.[0]?.genres;
            const genreOptions = Array.isArray(rawGenres)
                ? rawGenres
                    .filter((genre): genre is string => typeof genre === "string")
                : [];

            const normalizedGenres = [...genreOptions];
            if (!normalizedGenres.includes("All Genres")) {
                normalizedGenres.unshift("All Genres");
            }

            const defaultGenre = normalizedGenres.find(genre => genre !== "All Genres") || "";

            this.setState({
                genres: normalizedGenres,
                selectedGenre: defaultGenre,
                isGenresLoading: false
            }, () => {
                if (defaultGenre) {
                    this.fetchExplorerData(defaultGenre);
                }
            });
        } catch (error) {
            Logger.error("Error initializing content genre explorer", error);
            this.setState({
                error: "Failed to load content genre explorer",
                isGenresLoading: false
            });
        }
    };

    private handleFilterChange = (filters: AnalyticsFilters): void => {
        this.setState({ filters });
    };

    private handleApplyFilters = (): void => {
        this.setState(prevState => ({
            appliedFilters: { ...prevState.filters }
        }), () => {
            const { selectedGenre } = this.state;
            if (selectedGenre) {
                this.fetchExplorerData(selectedGenre);
            }
        });
    };

    private handleGenreChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const selectedGenre = event.target.value;
        this.setState({ selectedGenre }, () => {
            if (selectedGenre) {
                this.fetchExplorerData(selectedGenre);
            } else {
                this.setState({ metricsData: [], topBrandsData: [] });
            }
        });
    };

    private fetchExplorerData = async (selectedGenre: string): Promise<void> => {
        const { appliedFilters } = this.state;

        try {
            this.setState({ isLoading: true, error: null });

            const [metricsResponse, topBrandsResponse] = await Promise.all([
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "content_genre_explorer_metrics",
                    appliedFilters.startDate,
                    appliedFilters.endDate,
                    appliedFilters.attributionId,
                    appliedFilters.brandId,
                    appliedFilters.deviceType,
                    undefined,
                    undefined,
                    selectedGenre === "All Genres" ? undefined : { genre: selectedGenre }
                ),
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "content_genre_explorer_top_brands",
                    appliedFilters.startDate,
                    appliedFilters.endDate,
                    appliedFilters.attributionId,
                    appliedFilters.brandId,
                    appliedFilters.deviceType,
                    undefined,
                    undefined,
                    selectedGenre === "All Genres" ? undefined : { genre: selectedGenre }
                )
            ]);

            const metricsResult = (metricsResponse as any)?.result;
            const metricsData = Array.isArray(metricsResult) ? metricsResult : [];

            const topBrandsRaw = (topBrandsResponse as any)?.result;
            const topBrandsData = (Array.isArray(topBrandsRaw) ? topBrandsRaw : [])
                .map((row: Record<string, any>) => ({
                    thingBrandName: String(row?.thing_brand_name || row?.thing_brandname || "Unknown"),
                    plays: this.toNumberOrNull(row?.plays) ?? 0
                }))
                .sort((a: TopBrandPoint, b: TopBrandPoint) => b.plays - a.plays)
                .slice(0, 25);

            this.setState({ metricsData, topBrandsData });
        } catch (error) {
            Logger.error("Error fetching content genre explorer metrics", error);
            this.setState({ error: "Failed to load content genre explorer metrics" });
        } finally {
            this.setState({ isLoading: false });
        }
    };

    private toTitleCase = (value: string): string => {
        return value
            .replace(/_/g, " ")
            .replace(/\b\w/g, char => char.toUpperCase());
    };

    private toNumberOrNull = (value: unknown): number | null => {
        if (typeof value === "number" && Number.isFinite(value)) {
            return value;
        }
        if (typeof value === "string") {
            const parsed = Number(value);
            if (Number.isFinite(parsed)) {
                return parsed;
            }
        }
        return null;
    };

    private getMetricCards = (): GenreMetricCard[] => {
        const { metricsData } = this.state;
        if (!Array.isArray(metricsData) || metricsData.length === 0) {
            return [];
        }

        const sourceRow = metricsData[0] as Record<string, any>;
        const ignoredKeys = new Set([
            "genre",
            "genre_name",
            "genreName",
            "date",
            "datepartition"
        ]);

        const candidateKeys = Object.keys(sourceRow)
            .filter(key => !ignoredKeys.has(key))
            .filter(key => !key.startsWith("previous_"))
            .filter(key => !key.endsWith("_diff"))
            .filter(key => !key.endsWith("_pop_Pct"))
            .filter(key => !key.endsWith("_pop_pct"))
            .filter(key => !key.endsWith("_percent_change"))
            .filter(key => !key.endsWith("_change"))
            .filter(key => this.toNumberOrNull(sourceRow[key]) !== null)
            .slice(0, 8);

        return candidateKeys.map((key): GenreMetricCard => {
            const value = this.toNumberOrNull(sourceRow[key]);
            const percentChange = this.toNumberOrNull(sourceRow[`${key}_pop_Pct`])
                ?? this.toNumberOrNull(sourceRow[`${key}_pop_pct`])
                ?? this.toNumberOrNull(sourceRow[`${key}_percent_change`])
                ?? this.toNumberOrNull(sourceRow[`percent_change_${key}`]);
            const explicitNumericChange = this.toNumberOrNull(sourceRow[`${key}_diff`])
                ?? this.toNumberOrNull(sourceRow[`${key}_change`]);
            const previousValue = this.toNumberOrNull(sourceRow[`previous_${key}`]);
            const numericChange = explicitNumericChange
                ?? (value !== null && previousValue !== null ? value - previousValue : null);

            const isPercentMetric = key.includes("percent") || key.includes("rate");
            const isDecimalMetric = key.includes("per_") && !isPercentMetric;

            return {
                key,
                label: this.toTitleCase(key),
                value,
                changePercent: percentChange,
                numericChange,
                valueFormat: isPercentMetric ? "percent" : isDecimalMetric ? "decimal" : "number"
            };
        });
    };

    private getBrandBarColor = (index: number, total: number): string => {
        if (total <= 1) {
            return "#007bff";
        }

        const startHue = 205; // blue
        const endHue = 285; // purple
        const hue = startHue + ((endHue - startHue) * index) / (total - 1);

        return `hsl(${hue}, 78%, 52%)`;
    };

    public render(): ReactNode {
        const { filters, filterOptions, genres, selectedGenre, isGenresLoading, isLoading, error, topBrandsData } = this.state;
        const metricCards = this.getMetricCards();

        if (isGenresLoading) {
            return (
                <Container className="mw-100" key="genre-explorer" style={{ paddingBottom: "40px" }}>
                    <Row>
                        <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                            <h2>Genre Explorer</h2>
                        </Col>
                    </Row>
                    <Row className="justify-content-center">
                        <Col className="text-center" style={{ padding: "32px" }}>
                            <Spinner animation="border" variant="primary" />
                        </Col>
                    </Row>
                </Container>
            );
        }

        return (
            <Container className="mw-100" key="genre-explorer" style={{ paddingBottom: "40px" }}>
                <Row>
                    <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                        <h2>Genre Explorer</h2>
                    </Col>
                </Row>

                <AnalyticsFiltersComponent
                    filters={filters}
                    filterOptions={filterOptions}
                    onFilterChange={this.handleFilterChange}
                    onApplyFilters={this.handleApplyFilters}
                    customerConfig={this.props.customerConfig}
                    enableGroupBy={{}}
                />

                <Row style={{ marginTop: "8px", marginBottom: "20px" }}>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Genre</Form.Label>
                            <Form.Control
                                as="select"
                                value={selectedGenre}
                                onChange={this.handleGenreChange}
                                disabled={genres.length === 0 || isLoading || isGenresLoading}
                            >
                                {genres.length === 0 ? (
                                    <option value="">No genres available</option>
                                ) : (
                                    genres.map(genre => (
                                        <option key={genre} value={genre}>{genre}</option>
                                    ))
                                )}
                            </Form.Control>
                        </Form.Group>
                    </Col>
                </Row>

                {isLoading ? (
                    <Row className="justify-content-center">
                        <Col className="text-center" style={{ padding: "32px" }}>
                            <Spinner animation="border" variant="primary" />
                        </Col>
                    </Row>
                ) : error ? (
                    <Row>
                        <Col>
                            <Alert variant="danger">{error}</Alert>
                        </Col>
                    </Row>
                ) : (
                    <>

                        {metricCards.length > 0 ? (
                            <Row>
                                <Col>
                                    <MetricCards
                                        metrics={metricCards as MetricCardItem[]}
                                        theme={{
                                            background: "transparent",
                                            text: "#ffffff",
                                            chartBg: "rgba(0,0,0,0.2)",
                                            border: "rgba(255,255,255,0.15)"
                                        }}
                                        minCardWidth="220px"
                                        combineChangeAndDelta={true}
                                        noChangeLabel="No change data"
                                    />
                                </Col>
                            </Row>
                        ) : (
                            <Row>
                                <Col>
                                    <Alert variant="warning">No numeric metrics available to render as KPI widgets.</Alert>
                                </Col>
                            </Row>
                        )}

                        <Row style={{ marginTop: "20px" }}>
                            <Col>
                                <h5 style={{ marginBottom: "12px" }}>Top Brands by Plays</h5>
                                {topBrandsData.length === 0 ? (
                                    <Alert variant="info">No top brand data available for this genre and date range.</Alert>
                                ) : (
                                    <div
                                        style={{
                                            border: "1px solid rgba(255,255,255,0.15)",
                                            borderRadius: "8px",
                                            padding: "16px",
                                            background: "rgba(0,0,0,0.2)"
                                        }}
                                    >
                                        <ResponsiveContainer width="100%" height={Math.max(560, topBrandsData.length * 28)}>
                                            <BarChart
                                                data={topBrandsData}
                                                layout="vertical"
                                                margin={{ top: 5, right: 24, left: 12, bottom: 5 }}
                                            >
                                                <CartesianGrid stroke="rgba(255,255,255,0.12)" strokeDasharray="3 3" />
                                                <XAxis
                                                    type="number"
                                                    tick={{ fill: "#ffffff" }}
                                                    axisLine={{ stroke: "rgba(255,255,255,0.2)" }}
                                                    tickLine={{ stroke: "rgba(255,255,255,0.2)" }}
                                                    tickFormatter={(value: number) => value.toLocaleString()}
                                                />
                                                <YAxis
                                                    type="category"
                                                    dataKey="thingBrandName"
                                                    width={280}
                                                    interval={0}
                                                    tick={{ fill: "#ffffff", fontSize: 12 }}
                                                    axisLine={{ stroke: "rgba(255,255,255,0.2)" }}
                                                    tickLine={{ stroke: "rgba(255,255,255,0.2)" }}
                                                />
                                                <Tooltip
                                                    formatter={(value: number | string | undefined) => [Number(value ?? 0).toLocaleString(), "Plays"]}
                                                    contentStyle={{
                                                        backgroundColor: "#1f1f1f",
                                                        border: "1px solid rgba(255,255,255,0.2)",
                                                        color: "#ffffff"
                                                    }}
                                                    labelStyle={{ color: "#ffffff" }}
                                                    itemStyle={{ color: "#ffffff" }}
                                                />
                                                <Bar dataKey="plays">
                                                    {topBrandsData.map((entry: TopBrandPoint, index: number) => (
                                                        <Cell
                                                            key={`brand-bar-${entry.thingBrandName}-${index}`}
                                                            fill={this.getBrandBarColor(index, topBrandsData.length)}
                                                        />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </>
                )}
            </Container>
        );
    }
}

export default ContentGenreExplorer;